// La langue d'interface, disponible partout sans la faire descendre écran par
// écran. Même patron que `formule.ts`, à une différence près : l'utilisateur
// PEUT la changer. Ce n'est pas une frontière de sécurité comme la formule
// (qui vient du serveur et n'est jamais un réglage), juste une préférence de
// compte — au même titre qu'un nom de signature ou un ton par défaut.
//
// POURQUOI SUR LE COMPTE (auth.db) ET PAS EN localStorage COMME LE THÈME.
// Le thème clair/sombre est volontairement local (cf. `theme.ts`) : une
// préférence d'écran peut différer d'un appareil à l'autre sans gêner
// personne. La langue, elle, doit suivre le consultant qui se connecte depuis
// un autre poste — la re-choisir à chaque fois rendrait le réglage inutile.
//
// AJOUTER UNE LANGUE : l'ajouter à LANGUES_UI (`packages/engine/src/langues.ts`),
// puis laisser TypeScript lister chaque table qui ne la connaît pas encore
// (`vocabulaire.ts`, `i18n.ts`, `labels.ts`, `format.ts`, les mails côté
// serveur). Elle n'est PROPOSÉE (sélecteur, détection du navigateur) qu'une
// fois dans LANGUES_OUVERTES : c'est le sas d'une traduction pas encore
// relue, servie seulement à qui la demande (`?lang=xx`) tant qu'elle y attend.

import { createContext, useContext } from 'react'
import { LANGUES_UI, type UiLang } from '@ai-job-fit/engine/langues'
import { post } from './api'

export type Lang = UiLang

/** Toutes les langues que l'interface sait parler (la liste du moteur, que
 *  partagent aussi le serveur et les mails). */
export const SUPPORTED_LANGS: Lang[] = [...LANGUES_UI]

/** Celles qu'on PROPOSE : le sélecteur et la détection du navigateur. Une
 *  langue traduite automatiquement n'y entre qu'après relecture (issue #137 :
 *  le néerlandais, l'italien et l'espagnol, ouverts le 2026-10-17). L'ouvrir,
 *  c'est l'ajouter ici, rien d'autre. */
export const LANGUES_OUVERTES: Lang[] = ['fr', 'en', 'nl', 'it', 'es']

/** Les boutons du sélecteur : les langues ouvertes, plus celle qu'on affiche
 *  si elle est cachée. Sans ça, un `?lang=nl` de relecture laisserait un
 *  sélecteur où aucun bouton n'est enfoncé. */
export function languesProposees(courante: Lang): Lang[] {
  return LANGUES_OUVERTES.includes(courante) ? LANGUES_OUVERTES : [...LANGUES_OUVERTES, courante]
}

export function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && (SUPPORTED_LANGS as string[]).includes(v)
}

export interface LangContexte {
  lang: Lang
  /** Change la langue : met à jour le contexte tout de suite (optimiste,
   *  comme le reste de l'app) et persiste côté serveur si un compte est
   *  connecté. Sans effet de persistance avant connexion — cf. `Login.tsx`,
   *  qui gère sa propre détection tant qu'il n'y a pas de compte à qui
   *  l'attacher. */
  setLang: (lang: Lang) => void
}

/** Défaut 'fr' : c'est la langue actuelle du produit, donc un composant monté
 *  hors contexte (un test, un écran isolé) affiche ce qu'il affiche déjà
 *  aujourd'hui, plutôt qu'un anglais que personne n'a demandé. */
export const LangContext = createContext<LangContexte>({ lang: 'fr', setLang: () => {} })

export function useLang(): Lang {
  return useContext(LangContext).lang
}

export function useSetLang(): (lang: Lang) => void {
  return useContext(LangContext).setLang
}

/** Détecte une langue de repli AVANT toute connexion : `?lang=` d'abord (même
 *  mécanique que `site/index.html`, pour rester cohérent sur tout le
 *  produit), puis `navigator.language`, puis 'fr'. N'est appelée qu'une fois,
 *  à l'état initial : après ça, c'est soit un choix explicite, soit la valeur
 *  du compte qui commandent. */
export function detecterLangNavigateur(): Lang {
  try {
    const force = new URLSearchParams(location.search).get('lang')
    if (isLang(force)) return force
  } catch {
    // `location` absente (rendu hors navigateur, test) : on continue sans.
  }
  try {
    const preferees = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]
    for (const l of preferees) {
      const court = String(l || '').slice(0, 2).toLowerCase()
      if (isLang(court) && LANGUES_OUVERTES.includes(court)) return court
    }
  } catch {
    // Navigateur restreint : on retombe sur le français plus bas.
  }
  return 'fr'
}

/** Persiste la langue du compte connecté. Volontairement permissive côté
 *  erreur : une bascule qui échoue à s'enregistrer reste visible à l'écran
 *  (le contexte a déjà changé, optimiste), seule la mémorisation entre deux
 *  sessions est perdue — l'appelant journalise s'il veut faire mieux. */
export async function persistLang(lang: Lang): Promise<void> {
  await post('/api/auth/lang', { lang })
}
